import { CompanyType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import * as yup from 'yup';

import { AutocompleteBooleanOptionValue } from '~components/FormFields/CommonAutocompletes/AutocompleteBoolean';
import { humanReadableCompanyTypes } from '~components/FormFields/CommonAutocompletes/AutocompleteCompanyTypes';
import { AutocompleteFieldOption } from '~components/FormFields/v2/Autocomplete/types';
import { Account } from '~hooks/useAccount/models';
import { CompanyShareableBasic } from '~hooks/useCompanyShares/models';

export const accountConfigurationSchema = yup.object().shape({
  isConnectedAccount: yup.boolean().required(),
  companyType: yup.object().when('isConnectedAccount', {
    is: true,
    then: (schema) => {
      return schema
        .shape({ label: yup.string().required(), value: yup.string().required() })
        .required(t('form_validation_errors.required_simple'));
    },
    otherwise: (schema) => {
      return schema.notRequired();
    },
  }),
  drivers: yup
    .array()
    .of(
      yup
        .object()
        .shape({ label: yup.string().required(), value: yup.string().required() })
        .required(),
    )
    .notRequired(),
  equipment: yup
    .array()
    .of(
      yup
        .object()
        .shape({ label: yup.string().required(), value: yup.string().required() })
        .required(),
    )
    .notRequired(),
  autoAcceptOrders: yup
    .object()
    .shape({ label: yup.string().required(), value: yup.string().required() })
    .required(),
  showUserPhoneNumber: yup.boolean().notRequired(),
});
export type AccountConfigurationDto = yup.InferType<typeof accountConfigurationSchema>;

export function setAccountConfigurationSchemaDefaultValues(
  account?: Account,
  sharedDrivers?: CompanyShareableBasic[],
  sharedEquipment?: CompanyShareableBasic[],
) {
  const companyType = account?.connectedCompany?.companyType as CompanyType | undefined;
  const companyTypeDefaltValue = companyType
    ? { label: humanReadableCompanyTypes[companyType], value: companyType }
    : null;

  const isAutoAcceptOrdersEnabled =
    (typeof account?.autoAcceptOrders === 'boolean' && account.autoAcceptOrders) ||
    (typeof account?.autoAcceptOrders === 'object' && account.autoAcceptOrders.value);

  // In Edit mode reflect the saved value; new accounts default to portal access on.
  const isConnectedAccountDefault = account ? Boolean(account.connectedCompany) : true;

  const defaultValues: AccountConfigurationDto = {
    isConnectedAccount: isConnectedAccountDefault,
    companyType: companyTypeDefaltValue as AutocompleteFieldOption<CompanyType>,
    drivers: (sharedDrivers ?? []).map((item) => ({ label: item.name, value: item.id })),
    equipment: (sharedEquipment ?? []).map((item) => ({
      label: item.name,
      value: item.id,
    })),
    autoAcceptOrders: isAutoAcceptOrdersEnabled
      ? { label: t('actions.yes'), value: AutocompleteBooleanOptionValue.TRUE }
      : { label: t('actions.no'), value: AutocompleteBooleanOptionValue.FALSE },
    showUserPhoneNumber: account?.showUserPhoneNumber ?? true,
  };

  return defaultValues;
}
